import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DataTable, DataTableValue } from 'primereact/datatable';
import { Column, ColumnBodyOptions } from 'primereact/column';
import { formatNumber } from '@agro1desenvolvimento/utils-js';
import { chain, get, noop } from 'lodash';
import Yup from '../../../../utils/yup';
import { StepComponent } from '../types';
import { useForm } from '../../../../components/Form/hooks';
import { DEFAULT_VALUES, FormAdubacao } from './form-controller';
import Form from './form';
import ActionButtonsTemplate from '../components/action-buttons-template';
import { doseTemplate } from '../utils';
import { AdubacaoRow, AdubacaoTalhao, TalhaoRow } from '../../../../@types';
import defaultColumnProps from '../../../../utils/default-column-props';
import Agro1Toast from '../../../../components/Agro1Toast';

const VALIDATION_SCHEMA = Yup.object().shape({
  talhao: Yup.object().nullable(),
  area: Yup.number()
    .positive()
    .nullable()
    .when(['talhao', '$talhoes'], (params, schema) => {
      const [talhao, talhoes] = params as [AdubacaoTalhao, TalhaoRow[]];

      if (!talhao) return schema;

      const areaTotal = talhoes
        .filter(({ talhao: gleba }) => gleba?.id === talhao.talhao?.id)
        .reduce((soma, current) => soma + (current.area || 0), 0);

      return schema.required().max(areaTotal);
    }),
  adubo: Yup.object()
    .required()
    .test('unique', async (value, context) => {
      if (!('id' in value)) return true;

      if (!context.parent.talhao) {
        const aduboUtilizado = context.options.context?.talhoes.every(
          (talhao: TalhaoRow) =>
            context.options.context?.adubos.find(
              (adubo: AdubacaoRow) =>
                adubo.talhao?.talhao?.id === talhao.talhao?.id &&
                value.id === adubo.adubo?.id,
            ),
        );

        return !aduboUtilizado
          ? true
          : context.createError({
              path: context.path,
              message: 'Adubo já inserido para todos os talhões',
            });
      }

      const identification = `${context.parent.talhao.talhao?.id}-${value.id}`;

      if (context.parent.identification === identification) return true;

      const aduboDisponivel = context.options.context?.adubos.every(
        (item: AdubacaoRow) => item.identification !== identification,
      );

      return aduboDisponivel
        ? true
        : context.createError({
            path: context.path,
            message: 'Adubo já inserido para este talhão',
          });
    }),
  dose: Yup.number().positive().required(),
  unidade: Yup.object().required(),
});

const NUMBER_FORMAT_OPTIONS = {
  maximumFractionDigits: 2,
  useGrouping: true,
};

export const areaTemplate = (
  rowData: AdubacaoRow,
  options: ColumnBodyOptions,
) => {
  const areaFormatted = formatNumber(
    get(rowData, options.field),
    NUMBER_FORMAT_OPTIONS,
  );

  return `${areaFormatted} ha`;
};

const Adubacao: StepComponent = ({ formObject, recomendacaoSemeadura }) => {
  const [talhoes, adubos] = formObject.form.watch(['talhoes', 'adubos']);
  const contextForm = useMemo(() => ({ adubos, talhoes }), [adubos, talhoes]);
  const formAdubacao = useForm<AdubacaoRow>({
    validationSchema: VALIDATION_SCHEMA,
    defaultValues: DEFAULT_VALUES,
    context: contextForm,
  });
  const {
    reset,
    getValues,
    formState: { isSubmitSuccessful },
  } = formAdubacao.form;
  const formContainerRef = useRef<HTMLDivElement>(null);
  const [selectedAdubo, setSelectedAdubo] = useState<DataTableValue>();
  const [editingAduboIdentification, setEditingAduboIdentification] = useState<
    string | null
  >(null);

  const edit = useCallback(
    (data: AdubacaoRow) => {
      reset(data, { keepDefaultValues: true });
      formContainerRef.current?.scrollIntoView();
      setEditingAduboIdentification(data.identification);
      setSelectedAdubo(data);
    },
    [reset],
  );

  const remove = useCallback(
    (identification: string | null) => {
      if (!identification) return;

      const filteredAdubos = formObject.form
        .getValues('adubos')
        .filter((item) => item.identification !== identification);

      formObject.form.setValue('adubos', filteredAdubos);

      Agro1Toast.show({
        detail: 'Registro excluído com sucesso.',
        severity: 'success',
      });
    },
    [formObject.form],
  );

  const ActionBodyTemplate = useCallback(
    (rowData: AdubacaoRow) => (
      <ActionButtonsTemplate
        rowData={rowData}
        disabled={!!editingAduboIdentification}
        onEdit={edit}
        onRemove={remove}
      />
    ),
    [editingAduboIdentification, edit, remove],
  );

  const setAdubosPorTalhao = useCallback(
    (data: AdubacaoRow) => {
      const itens = [...formObject.form.getValues('adubos')];

      const newAdubos = chain(talhoes)
        .filter(
          (talhao) =>
            !itens.find(
              (adubo) =>
                talhao.talhao?.id === adubo.talhao?.talhao?.id &&
                adubo.adubo?.id === data.adubo?.id,
            ),
        )
        .sortBy('dataInicio')
        .reduce<AdubacaoRow[]>((novosAdubos, current) => {
          const identification = `${current.talhao?.id}-${data.adubo?.id}`;

          const index = novosAdubos.findIndex(
            (item) => item.identification === identification,
          );

          if (index > -1) {
            novosAdubos[index] = {
              ...novosAdubos[index],
              area: (novosAdubos[index].area || 0) + (current.area || 0),
            };
          } else {
            const talhao = {
              id: current.identification,
              talhao: current.talhao,
              area: current.area,
            };

            novosAdubos.push({
              ...data,
              identification,
              talhao,
              area: current.area,
            });
          }

          return novosAdubos;
        }, [])
        .value();

      formObject.form.setValue('adubos', [...itens, ...newAdubos]);

      Agro1Toast.show({
        detail: 'Registros salvos com sucesso.',
        severity: 'success',
      });
    },
    [talhoes, formObject.form],
  );

  const onSubmitHandler = () => {
    const data = getValues();

    if (!data.adubo) return;

    if (!data.talhao) {
      setAdubosPorTalhao(data);
      reset();
      return;
    }

    const itens = [...formObject.form.getValues('adubos')];
    const newAdubo = {
      ...data,
      identification: `${data.talhao.talhao?.id}-${data.adubo.id}`,
    };

    if (editingAduboIdentification) {
      const index = itens.findIndex(
        ({ identification }) => identification === editingAduboIdentification,
      );

      itens[index] = newAdubo;
      formObject.form.setValue('adubos', itens);
      setEditingAduboIdentification(null);

      Agro1Toast.show({
        detail: 'Registro alterado com sucesso.',
        severity: 'success',
      });

      reset();

      return;
    }

    formObject.form.setValue('adubos', [...itens, newAdubo]);

    Agro1Toast.show({
      detail: 'Registro salvo com sucesso.',
      severity: 'success',
    });

    reset();
  };

  useEffect(() => {
    if (isSubmitSuccessful) {
      setSelectedAdubo(undefined);
      reset();
    }
  }, [isSubmitSuccessful, reset]);

  const onCancelEditing = () => {
    reset();
    setEditingAduboIdentification(null);
    setSelectedAdubo(undefined);
  };

  return (
    <FormAdubacao formObject={formAdubacao} onSubmit={noop}>
      <div ref={formContainerRef}>
        <Form
          formObject={formObject}
          recomendacaoSemeadura={recomendacaoSemeadura}
          isEditing={!!editingAduboIdentification}
          onSubmit={onSubmitHandler}
          onCancel={onCancelEditing}
        />
      </div>
      <DataTable
        removableSort
        value={adubos}
        className="mt-5 p-0 data-table"
        emptyMessage="Nenhum adubo adicionado."
        selectionMode="single"
        selection={selectedAdubo}
      >
        <Column
          {...defaultColumnProps('left')}
          field="talhao.talhao.descricao"
          header="Talhão"
        />
        <Column
          {...defaultColumnProps('left')}
          field="adubo.descricao"
          header="Adubo"
        />
        <Column
          {...defaultColumnProps()}
          field="dose"
          header="Dose"
          body={doseTemplate}
        />
        <Column
          {...defaultColumnProps()}
          field="area"
          header="Área"
          body={areaTemplate}
        />
        <Column align="center" body={ActionBodyTemplate} />
      </DataTable>
    </FormAdubacao>
  );
};

Adubacao.validationSchema = Yup.object();

export default Adubacao;
